import React, { useEffect, useMemo, useState } from 'react';
import { message } from 'antd';
import {
  fractureAPI,
  type FractureDesignBasisResponse,
  type GeologyCandidateDesign,
  type PublishedNaturalFractureModel,
} from '../../services/api';
import {
  OPTIMIZATION_VARIABLES,
  SENSITIVITY_PARAMETERS,
  combinationCount,
  parseScenarioValues,
  type ParameterSweepResponse,
  type ScenarioResult,
  type SensitivityResponse,
} from './scenarioAnalysis';

const control: React.CSSProperties = { width: '100%', boxSizing: 'border-box', padding: '5px 6px', fontSize: 10, border: '1px solid #D6DAE0', borderRadius: 3 };
const button: React.CSSProperties = { padding: '6px 8px', fontSize: 10, border: '1px solid #D6DAE0', borderRadius: 3, background: '#F4F6F9', color: '#4F6680', cursor: 'pointer' };
const th: React.CSSProperties = { padding: '4px 3px', textAlign: 'right', borderBottom: '1px solid #D6DAE0', color: '#4F6680', whiteSpace: 'nowrap' };
const td: React.CSSProperties = { padding: '4px 3px', textAlign: 'right', borderBottom: '1px solid #ECEEF1', fontFamily: 'monospace', whiteSpace: 'nowrap' };
const helpBox: React.CSSProperties = { padding: 7, background: '#F4F6F9', color: '#4F6680', fontSize: 10, lineHeight: 1.6 };

const CONSTRAINT_LABELS: Record<string, string> = {
  minStageLength: '最小段长', maxStageLength: '最大段长', minClusterSpacing: '最小簇间距',
  maxClusterSpacing: '最大簇间距', minClusterCount: '最小簇数', maxClusterCount: '最大簇数',
  maxClusterSpan: '最大簇展布长度', maxPerforationInterval: '最大射孔段长度',
  minRate: '最小施工排量', maxRate: '最大施工排量', maxFluidVolume: '最大总液量',
  maxProppantMass: '最大总砂量', maxScenarioCount: '最大候选数',
  allowCrossSweetSpotBoundary: '允许跨甜点边界', allowCrossStressBarrier: '允许跨应力屏障',
  stressJumpWarningMpa: '应力突变提示值',
};
const CONSTRAINT_DESCRIPTIONS: Record<string, string> = {
  minStageLength: '控制分段不要短于可施工和可隔离的有效长度，属于方案生成边界。',
  maxStageLength: '限制单段覆盖范围，避免段内地质差异和施工风险过大，属于方案生成边界。',
  minClusterSpacing: '避免簇间过密导致应力干扰和进液竞争过强，属于布簇设计边界。',
  maxClusterSpacing: '避免簇间过疏造成储层改造覆盖不连续，属于布簇设计边界。',
  minClusterCount: '保证单段具有基本改造覆盖，属于方案生成边界。',
  maxClusterCount: '限制单段流量分配、射孔摩阻和施工复杂度，属于方案生成边界。',
  minRate: '保证裂缝开启和携砂所需的最低施工能力，属于施工可行性边界。',
  maxRate: '受泵组、井口限压和管柱水力条件约束，属于施工可行性边界。',
  maxClusterSpan: '限制单段内首末簇的最大展布范围，属于布簇设计边界。',
  maxPerforationInterval: '限制单段射孔覆盖长度，属于完井与施工设计边界。',
  maxFluidVolume: '限制候选方案的单段或方案液量规模，不直接作为传播方程参数。',
  maxProppantMass: '限制候选方案的砂量规模，不直接作为传播方程参数。',
  maxScenarioCount: '限制方案搜索的计算规模，仅影响候选方案数量。',
  allowCrossSweetSpotBoundary: '控制自动分段能否跨越甜点评价边界，属于方案生成规则。',
  allowCrossStressBarrier: '控制自动分段能否跨越应力屏障，属于方案生成规则。',
  stressJumpWarningMpa: '用于设计阶段地应力突变提示，不是 PKN/P3D 传播求解阈值。',
};
const PRIMARY_CONSTRAINT_IDS = [
  'minStageLength', 'maxStageLength', 'minClusterSpacing', 'maxClusterSpacing',
  'minClusterCount', 'maxClusterCount', 'minRate', 'maxRate',
] as const;
const ADVANCED_CONSTRAINT_IDS = [
  'maxClusterSpan', 'maxPerforationInterval', 'maxFluidVolume', 'maxProppantMass',
  'maxScenarioCount', 'allowCrossSweetSpotBoundary', 'allowCrossStressBarrier', 'stressJumpWarningMpa',
] as const;
const DISPLAY_UNITS: Record<string, string> = {
  m: 'm', count: '', boolean: '', 'm3/min': 'm³/min', m3: 'm³', t: 't', MPa: 'MPa',
};
const FEASIBILITY_LABELS: Record<string, string> = {
  FEASIBLE: '可行', FEASIBLE_WITH_WARNINGS: '可行（有提示）', INFEASIBLE: '不可行', SIMULATION_FAILED: '模拟失败',
};
const RECOMMENDATION_LABELS: Record<string, string> = {
  FLOW_BALANCE_IMPROVED: '簇间流量分配更均衡',
  HEIGHT_CONTAINED: '缝高控制效果较好',
  EFFECTIVE_LENGTH_IMPROVED: '有效半缝长较大',
  NF_INTERACTION_REDUCED: '天然裂缝交互复杂度较低',
};

const METRICS = [
  ['effectiveHalfLength', '有效半缝长'], ['halfLength', '几何半缝长'], ['height', '裂缝高度'], ['flowBalance', '簇流量均衡度'],
  ['initiationBalance', '簇起裂均衡度'], ['stressShadowInterference', '应力阴影干扰'],
  ['encounterCount', '天然裂缝相交数'], ['crossRatio', '穿过比例'], ['deflectRatio', '转向比例'],
  ['interactionComplexityIndex', '交互复杂度'], ['conductivity', '导流能力（部分可用）'],
] as const;

const DEFAULT_WEIGHTS: Record<string, number> = {
  effectiveHalfLength: 0.20, halfLength: 0.02, heightContainment: 0.18, flowBalance: 0.20, initiationBalance: 0.16,
  stressShadowInterference: 0.10, fractureCoverage: 0.10, interactionComplexityIndex: 0.05,
  efficiency: 0.03, effectiveHalfLengthPerFluid: 0.02, conductivity: 0,
};
const METRIC_DIRECTIONS: Record<string, string> = {
  effectiveHalfLength: '↑', halfLength: '↑', heightContainment: '↑', flowBalance: '↑', initiationBalance: '↑',
  stressShadowInterference: '↓', fractureCoverage: '↑', interactionComplexityIndex: '↓',
  efficiency: '↑', effectiveHalfLengthPerFluid: '↑', conductivity: '↑',
};

const METRIC_LABELS: Record<string, string> = {
  effectiveHalfLength: '有效半缝长', halfLength: '几何半缝长', heightContainment: '缝高控制度',
  flowBalance: '簇流量均衡度', initiationBalance: '簇起裂均衡度',
  stressShadowInterference: '应力阴影干扰', fractureCoverage: '裂缝覆盖面积',
  interactionComplexityIndex: '天然裂缝交互复杂度', efficiency: '压裂液效率',
  effectiveHalfLengthPerFluid: '单位液量有效半缝长', conductivity: '导流能力（部分可用）',
};

const OBJECTIVE_WEIGHTS: Record<Props['objectiveTemplate'], Record<string, number>> = {
  BALANCED_STIMULATION: DEFAULT_WEIGHTS,
  LENGTH_PRIORITY: { ...DEFAULT_WEIGHTS, effectiveHalfLength: 0.38, heightContainment: 0.12, flowBalance: 0.14, initiationBalance: 0.10 },
  CONTAINMENT_PRIORITY: { ...DEFAULT_WEIGHTS, effectiveHalfLength: 0.16, heightContainment: 0.38, flowBalance: 0.14, initiationBalance: 0.12 },
  CLUSTER_BALANCE_PRIORITY: { ...DEFAULT_WEIGHTS, effectiveHalfLength: 0.14, heightContainment: 0.14, flowBalance: 0.32, initiationBalance: 0.24 },
};

const format = (value: unknown, digits = 3) => value === null || value === undefined || !Number.isFinite(Number(value)) ? '—' : Number(value).toFixed(digits);

const csvDownload = (filename: string, rows: Array<Record<string, unknown>>) => {
  if (!rows.length) return;
  const keys = [...new Set(rows.flatMap((row) => Object.keys(row)))];
  const quote = (value: unknown) => `"${String(value ?? '').replace(/"/g, '""')}"`;
  const csv = [keys.map(quote).join(','), ...rows.map((row) => keys.map((key) => quote(row[key])).join(','))].join('\r\n');
  const url = URL.createObjectURL(new Blob([`\ufeff${csv}`], { type: 'text/csv;charset=utf-8' }));
  const anchor = document.createElement('a');
  anchor.href = url; anchor.download = filename; anchor.click(); URL.revokeObjectURL(url);
};

function SensitivityCurve({ cases, metric }: { cases: ScenarioResult[]; metric: string }) {
  const points = cases.filter((item) => item.status === 'SUCCEEDED' && item.value !== undefined && item.metrics?.[metric as keyof typeof item.metrics] != null)
    .map((item) => ({ x: Number(item.value), y: Number(item.metrics?.[metric as keyof typeof item.metrics]) }));
  if (points.length < 2) return <div style={{ padding: 8, color: '#8598AD', fontSize: 10 }}>至少需要两个成功算例才能显示敏感性曲线。</div>;
  const xs = points.map((point) => point.x); const ys = points.map((point) => point.y);
  const xmin = Math.min(...xs); const xmax = Math.max(...xs); const ymin = Math.min(...ys); const ymax = Math.max(...ys);
  const coordinates = points.map((point) => `${10 + 180 * (point.x - xmin) / Math.max(1e-12, xmax - xmin)},${52 - 42 * (point.y - ymin) / Math.max(1e-12, ymax - ymin)}`).join(' ');
  return <svg viewBox="0 0 200 62" style={{ width: '100%', height: 70, border: '1px solid #ECEEF1', background: '#fff' }} aria-label="Sensitivity Curve">
    <polyline fill="none" stroke="#6A4C93" strokeWidth="2" points={coordinates} />
    {coordinates.split(' ').map((point, index) => { const [cx, cy] = point.split(','); return <circle key={index} cx={cx} cy={cy} r="2.5" fill="#C2533A" />; })}
  </svg>;
}

type Props = {
  designId: number | null;
  simulationId: number | null;
  baseOptions: Record<string, unknown>;
  naturalFracturePhysics: boolean;
  naturalFractureModel: PublishedNaturalFractureModel | null;
  activeSweetSpotRun: Record<string, any> | null;
  designMode: 'MANUAL' | 'GEOLOGY_CONSTRAINED';
  onDesignModeChange: (value: 'MANUAL' | 'GEOLOGY_CONSTRAINED') => void;
  workflowStep: 'DESIGN_BASIS' | 'SENSITIVITY' | 'OPTIMIZATION' | 'COMPARISON' | 'FULL_WELL';
  onWorkflowStepChange: (value: Props['workflowStep']) => void;
  engineeringConstraints: Record<string, { value: number | boolean; unit: string; source: string }>;
  onEngineeringConstraintsChange: (value: Props['engineeringConstraints']) => void;
  objectiveTemplate: 'BALANCED_STIMULATION' | 'LENGTH_PRIORITY' | 'CONTAINMENT_PRIORITY' | 'CLUSTER_BALANCE_PRIORITY';
  onObjectiveTemplateChange: (value: Props['objectiveTemplate']) => void;
  selectedVariables: string[];
  onSelectedVariablesChange: (value: string[]) => void;
  topN: 3 | 5 | 10;
  onTopNChange: (value: 3 | 5 | 10) => void;
  comparisonSchemeIds: string[];
  onComparisonSchemeIdsChange: (value: string[]) => void;
  onComparisonSimulationIdsChange: (value: number[]) => void;
  onSelectSimulation: (simulationId: number, stageNumber?: number) => void;
  onSimulationsCreated: () => Promise<void>;
  onDesignVariantCreated: (designId: number) => Promise<void>;
};

export default function ScenarioWorkspace(props: Props) {
  const mode = props.workflowStep;
  const [sensitivityParameter, setSensitivityParameter] = useState('nf_orientation_offset_deg');
  const selectedSensitivity = SENSITIVITY_PARAMETERS.find((item) => item.id === sensitivityParameter) ?? SENSITIVITY_PARAMETERS[0];
  const [sensitivityValues, setSensitivityValues] = useState<string>(selectedSensitivity.defaults);
  const [sensitivityMetric, setSensitivityMetric] = useState('halfLength');
  const [engineeringOverride, setEngineeringOverride] = useState(false);
  const [sensitivityBusy, setSensitivityBusy] = useState(false);
  const [sensitivityResult, setSensitivityResult] = useState<SensitivityResponse | null>(null);
  const [variableValues, setVariableValues] = useState<Record<string, string>>(() => Object.fromEntries(OPTIMIZATION_VARIABLES.map((item) => [item.id, item.defaults])));
  const [weights, setWeights] = useState(DEFAULT_WEIGHTS);
  const [sweepBusy, setSweepBusy] = useState(false);
  const [sweepResult, setSweepResult] = useState<ParameterSweepResponse | null>(null);
  const [baselineSimulationId, setBaselineSimulationId] = useState<number | null>(null);
  const [designBasisBusy, setDesignBasisBusy] = useState(false);
  const [designBasis, setDesignBasis] = useState<FractureDesignBasisResponse | null>(null);
  const [selectedCandidateId, setSelectedCandidateId] = useState<string | null>(null);
  const [variantBusy, setVariantBusy] = useState(false);
  const count = combinationCount(variableValues, props.selectedVariables);
  const visibleConstraints = Object.keys(props.engineeringConstraints).length
    ? props.engineeringConstraints
    : designBasis?.constraints ?? {};

  const renderConstraint = (id: string) => {
    const item = visibleConstraints[id];
    if (!item) return null;
    const unit = DISPLAY_UNITS[item.unit] ?? item.unit;
    const label = `${CONSTRAINT_LABELS[id] ?? id}${unit ? `（${unit}）` : ''}`;
    const updateValue = (value: number | boolean) => props.onEngineeringConstraintsChange({
      ...visibleConstraints,
      [id]: { ...item, value, source: 'USER' },
    });
    return <label key={id} title={CONSTRAINT_DESCRIPTIONS[id]} style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 66px', gap: 6, alignItems: 'center', fontSize: 10 }}>
      <span style={{ color: '#34495E', lineHeight: 1.25 }}>{label}</span>
      {typeof item.value === 'boolean'
        ? <input type="checkbox" checked={item.value} onChange={(event) => updateValue(event.target.checked)} />
        : <input aria-label={label} type="number" value={Number(item.value)} onChange={(event) => updateValue(Number(event.target.value))} style={control} />}
    </label>;
  };

  const refreshDesignBasis = async () => {
    if (!props.designId) return;
    setDesignBasisBusy(true);
    try {
      const { data } = await fractureAPI.getDesignBasis({
        design_id: props.designId,
        design_mode: props.designMode,
        sweet_spot_context: props.activeSweetSpotRun,
        natural_fracture_model_id: props.naturalFractureModel?.modelId,
        constraints: props.engineeringConstraints,
      });
      setDesignBasis(data);
      setSelectedCandidateId((current) => data.candidateDesigns.some((item) => item.candidateId === current)
        ? current : data.candidateDesigns[0]?.candidateId ?? null);
      if (!Object.keys(props.engineeringConstraints).length) {
        props.onEngineeringConstraintsChange(data.constraints as Props['engineeringConstraints']);
      }
    } catch (error: any) {
      message.error(error?.response?.data?.detail || '设计依据加载失败');
    } finally {
      setDesignBasisBusy(false);
    }
  };

  useEffect(() => {
    refreshDesignBasis();
    // Constraints are refreshed explicitly after editing to avoid request storms.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.designId, props.designMode, props.activeSweetSpotRun?.runId, props.naturalFractureModel?.modelId]);

  useEffect(() => {
    setWeights({ ...OBJECTIVE_WEIGHTS[props.objectiveTemplate] });
  }, [props.objectiveTemplate]);

  const selectableSchemes = useMemo(() => {
    const baseline: ScenarioResult[] = sweepResult?.baselineScheme
      ? [sweepResult.baselineScheme]
      : baselineSimulationId ? [{ schemeId: 'BASELINE', status: 'SUCCEEDED', simulationId: baselineSimulationId, parameters: {}, metrics: {} }] : [];
    return [...baseline, ...(sweepResult?.topSchemes ?? [])];
  }, [baselineSimulationId, sweepResult]);

  const updateComparison = (ids: string[]) => {
    props.onComparisonSchemeIdsChange(ids);
    props.onComparisonSimulationIdsChange(selectableSchemes.filter((scheme) => ids.includes(scheme.schemeId ?? '')).map((scheme) => scheme.simulationId).filter((id): id is number => Boolean(id)));
  };

  const runSensitivity = async () => {
    if (!props.designId) return message.warning('请先选择压裂方案');
    const values = parseScenarioValues(sensitivityValues);
    if (!values.length || values.length > 15) return message.warning('敏感性分析需要 1–15 个有效值');
    if (selectedSensitivity.kind === 'NF' && (!props.naturalFracturePhysics || !props.naturalFractureModel)) return message.warning('天然裂缝敏感性分析需要开启天然裂缝交互并选择已发布模型');
    setSensitivityBusy(true);
    try {
      const { data } = await fractureAPI.scenarioSensitivity({
        design_id: props.designId, parameter: sensitivityParameter, values, metric: sensitivityMetric,
        engineering_override_enabled: engineeringOverride,
        base_options: props.baseOptions,
      });
      setSensitivityResult(data as SensitivityResponse);
      await props.onSimulationsCreated();
      message.success(`敏感性分析完成：${data.completedCases}/${data.totalCases}`);
    } catch (error: any) {
      message.error(error?.response?.data?.detail || '敏感性分析失败');
    } finally { setSensitivityBusy(false); }
  };

  const runSweep = async () => {
    if (!props.designId) return message.warning('请先选择压裂方案');
    if (!props.selectedVariables.length || props.selectedVariables.length > 3) return message.warning('请选择 1–3 个工程变量');
    if (count > 25) return message.warning('当前组合数过多，请减少变量或取值');
    const variables = Object.fromEntries(props.selectedVariables.map((id) => [id, parseScenarioValues(variableValues[id] ?? '')]));
    if (Object.values(variables).some((values) => !values.length)) return message.warning('每个变量至少需要一个有效值');
    setSweepBusy(true); setBaselineSimulationId(props.simulationId);
    try {
      const optimizationRunId = typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? `OPT-${crypto.randomUUID()}` : `OPT-${Date.now()}`;
      const { data } = await fractureAPI.parameterSweep({
        design_id: props.designId,
        baseline_simulation_id: props.simulationId,
        variables,
        base_options: {
          ...props.baseOptions,
          sweet_spot_version: designBasis?.geologyContext?.sweetSpotVersion?.value,
          geology_context_version: designBasis?.geologyContext?.contextVersion,
          constraints: props.engineeringConstraints,
        },
        constraints: props.engineeringConstraints,
        objective_template: props.objectiveTemplate,
        optimization_run_id: optimizationRunId,
        weights,
        top_n: props.topN,
      });
      setSweepResult(data as ParameterSweepResponse);
      await props.onSimulationsCreated();
      const defaultSelection = [props.simulationId ? 'BASELINE' : '', ...(data.topSchemes ?? []).slice(0, 3).map((item: ScenarioResult) => item.schemeId)].filter(Boolean) as string[];
      props.onComparisonSchemeIdsChange(defaultSelection);
      props.onComparisonSimulationIdsChange([
        ...(props.simulationId ? [props.simulationId] : []),
        ...(data.topSchemes ?? []).slice(0, 3).map((item: ScenarioResult) => item.simulationId).filter((id: number | undefined): id is number => Boolean(id)),
      ]);
      message.success(`方案优化完成：${data.completedCases}/${data.totalCases}`);
    } catch (error: any) { message.error(error?.response?.data?.detail || '方案优化失败'); }
    finally { setSweepBusy(false); }
  };

  const applyRecommendedVariant = async (scheme: ScenarioResult | GeologyCandidateDesign) => {
    if (!props.designId) return;
    const feasibility = 'feasibilityStatus' in scheme ? scheme.feasibilityStatus : scheme.status;
    if (feasibility !== 'FEASIBLE' && feasibility !== 'FEASIBLE_WITH_WARNINGS') {
      return message.warning('只有工程可行方案可以创建方案变体');
    }
    setVariantBusy(true);
    try {
      const { data } = await fractureAPI.applyDesignVariant({
        design_id: props.designId,
        scheme,
        design_mode: props.designMode,
        source_optimization_run_id: sweepResult?.optimizationRunId,
        sweet_spot_version: designBasis?.geologyContext?.sweetSpotVersion?.value,
        geology_context_version: designBasis?.geologyContext?.contextVersion,
        natural_fracture_model_version: props.naturalFractureModel?.version,
      });
      const variantId = Number(data?.variant?.id);
      await props.onDesignVariantCreated(variantId);
      message.success(`已创建方案变体 #${variantId}，原方案未修改`);
    } catch (error: any) {
      message.error(error?.response?.data?.detail || '创建方案变体失败');
    } finally {
      setVariantBusy(false);
    }
  };

  return <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
    {mode === 'DESIGN_BASIS' && <>
      <label style={{ fontSize: 10 }}>设计模式
        <select value={props.designMode} onChange={(event) => props.onDesignModeChange(event.target.value as Props['designMode'])} style={control}>
          <option value="MANUAL">手工设计</option>
          <option value="GEOLOGY_CONSTRAINED">地质约束设计</option>
        </select>
      </label>
      <div style={{ padding: 7, background: '#F4F6F9', fontSize: 10, lineHeight: 1.6 }}>
        甜点评价：{props.activeSweetSpotRun ? '已关联有效版本' : '未关联'}<br />
        天然裂缝：{props.naturalFractureModel ? `${props.naturalFractureModel.name} v${props.naturalFractureModel.version}` : '未选择'}<br />
        现场标定：{designBasis?.fieldCalibration?.status === 'FIELD_CALIBRATION_AVAILABLE' ? '已关联' : '未关联'}
      </div>
      {designBasis?.manualFallback && <div style={{ color: '#A66A1F', fontSize: 10 }}>{designBasis.fallbackReason}</div>}
      <details open><summary style={{ fontSize: 10, fontWeight: 700, cursor: 'pointer' }}>分段与施工约束</summary>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 7 }}>
          {PRIMARY_CONSTRAINT_IDS.map(renderConstraint)}
        </div>
      </details>
      <details><summary style={{ fontSize: 10, cursor: 'pointer' }}>高级设计约束</summary>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 7 }}>
          {ADVANCED_CONSTRAINT_IDS.map(renderConstraint)}
        </div>
      </details>
      <button onClick={refreshDesignBasis} disabled={designBasisBusy} style={button}>{designBasisBusy ? '正在刷新设计依据…' : '刷新候选设计'}</button>
      <div style={{ overflowX: 'auto' }}><table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 10 }}><thead><tr>{['候选方案', '来源', '段数', '总簇数', '可行性', '问题数'].map((label) => <th key={label} style={th}>{label}</th>)}</tr></thead><tbody>
        {(designBasis?.candidateDesigns ?? []).map((candidate) => <tr key={candidate.candidateId} onClick={() => setSelectedCandidateId(candidate.candidateId)} style={{ cursor: 'pointer', background: candidate.candidateId === selectedCandidateId ? '#F4EFFA' : undefined }}>
          <td style={td}>{candidate.candidateId}</td><td style={td}>{candidate.source === 'MANUAL_DESIGN' ? '手工设计' : '地质约束'}</td><td style={td}>{candidate.stages.length}</td><td style={td}>{candidate.stages.reduce((sum, stage) => sum + stage.clusterPositionsMd.length, 0)}</td><td style={td}>{FEASIBILITY_LABELS[candidate.status] ?? candidate.status}</td><td style={td}>{candidate.constraintCount}</td>
        </tr>)}
      </tbody></table></div>
      {selectedCandidateId && (() => {
        const candidate = designBasis?.candidateDesigns.find((item) => item.candidateId === selectedCandidateId);
        if (!candidate) return null;
        return <>
          <div style={{ fontSize: 10, fontWeight: 700 }}>分段地质摘要</div>
          <div style={{ overflowX: 'auto' }}><table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 10 }}><thead><tr>{['段', 'MD', '簇数', '间距', 'SSI', 'Shmin', 'E', 'ν', 'NF', '屏障'].map((label) => <th key={label} style={th}>{label}</th>)}</tr></thead><tbody>{candidate.stages.map((stage) => {
            const geo = stage.geologySummary as Record<string, any>;
            return <tr key={stage.stageNumber}><td style={td}>{stage.stageNumber}</td><td style={td}>{stage.topMd}–{stage.bottomMd}</td><td style={td}>{stage.clusterPositionsMd.length}</td><td style={td}>{format(stage.clusterSpacingM, 1)}</td><td style={td}>{format(geo.ssiMean)}</td><td style={td}>{format(geo.shminMeanMpa, 1)}</td><td style={td}>{format(geo.youngsModulusMeanGpa, 1)}</td><td style={td}>{format(geo.poissonRatioMean)}</td><td style={td}>{geo.naturalFractureCount ?? '不可用'}</td><td style={td}>{geo.barrierStatus === 'SUGGESTED_BOUNDARY' ? '建议边界' : '无内部边界'}</td></tr>;
          })}</tbody></table></div>
          <button disabled={variantBusy || (candidate.status !== 'FEASIBLE' && candidate.status !== 'FEASIBLE_WITH_WARNINGS')} onClick={() => applyRecommendedVariant(candidate)} style={button}>创建方案变体</button>
        </>;
      })()}
      {designBasis?.postFracCapability && <div style={{ ...helpBox }}>
        压后支撑缝宽：{designBasis.postFracCapability.proppedWidth?.status}；
        导流能力：{designBasis.postFracCapability.conductivity?.status}（簇级工程估算）。
      </div>}
    </>}
    {mode === 'SENSITIVITY' && <>
      <div style={{ padding: 7, background: '#F4F6F9', fontSize: 10, lineHeight: 1.5 }}>
        基础天然裂缝模型：{props.naturalFractureModel ? `${props.naturalFractureModel.name} v${props.naturalFractureModel.version}` : '未选择'}<br />
        情景覆盖仅用于当前敏感性算例，不修改已发布模型或当前方案。
      </div>
      <label style={{ fontSize: 10 }}>分析参数<select value={sensitivityParameter} onChange={(event) => {
        const next = SENSITIVITY_PARAMETERS.find((item) => item.id === event.target.value) ?? SENSITIVITY_PARAMETERS[0];
        setSensitivityParameter(next.id); setSensitivityValues(next.defaults);
      }} style={control}>{SENSITIVITY_PARAMETERS.map((item) => <option key={item.id} value={item.id}>{item.label} ({item.unit})</option>)}</select></label>
      <label style={{ fontSize: 10 }}>取值列表<input value={sensitivityValues} onChange={(event) => setSensitivityValues(event.target.value)} style={control} /></label>
      <label style={{ fontSize: 10 }}>纵轴指标<select value={sensitivityMetric} onChange={(event) => setSensitivityMetric(event.target.value)} style={control}>{METRICS.map(([id, label]) => <option key={id} value={id}>{label}</option>)}</select></label>
      {'requiresOverride' in selectedSensitivity && selectedSensitivity.requiresOverride && <label style={{ fontSize: 10 }}><input type="checkbox" checked={engineeringOverride} onChange={(event) => setEngineeringOverride(event.target.checked)} /> 工程情景覆盖（已发布字段缺失时必须显式启用）</label>}
      <button onClick={runSensitivity} disabled={sensitivityBusy} style={button}>{sensitivityBusy ? '正在运行真实求解器算例…' : '运行单因素敏感性分析'}</button>
      {sensitivityResult && <>
        <div style={{ fontSize: 10 }}>完成 {sensitivityResult.completedCases}/{sensitivityResult.totalCases} · 失败 {sensitivityResult.failedCases} · 无效 {sensitivityResult.invalidCases}</div>
        <SensitivityCurve cases={sensitivityResult.cases} metric={sensitivityMetric} />
        <div style={{ overflowX: 'auto' }}><table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 10 }}><thead><tr>{['参数值', '半缝长', '缝高', '流量均衡', 'NF相交', '穿过', '转向', '状态'].map((label) => <th key={label} style={th}>{label}</th>)}</tr></thead><tbody>{sensitivityResult.cases.map((item) => <tr key={item.caseId} onClick={() => item.simulationId && props.onSelectSimulation(item.simulationId)} style={{ cursor: item.simulationId ? 'pointer' : 'default' }}><td style={td}>{item.value}</td><td style={td}>{format(item.metrics?.halfLength, 1)}</td><td style={td}>{format(item.metrics?.height, 1)}</td><td style={td}>{format(item.metrics?.flowBalance)}</td><td style={td}>{format(item.metrics?.encounterCount, 0)}</td><td style={td}>{format(item.metrics?.crossRatio)}</td><td style={td}>{format(item.metrics?.deflectRatio)}</td><td style={td}>{item.status}{item.cacheHit ? ' · 缓存' : ''}</td></tr>)}</tbody></table></div>
        <button style={button} onClick={() => csvDownload('fracsim-sensitivity-results.csv', sensitivityResult.cases.map((item) => ({ parameter: item.parameter, value: item.value, status: item.status, simulationId: item.simulationId, ...item.metrics })))}>导出敏感性 CSV</button>
      </>}
    </>}
    {mode === 'OPTIMIZATION' && <>
      <div style={{ fontSize: 10, color: '#4F6680' }}>选择 1–3 个工程变量；地质与天然裂缝参数只进入敏感性分析。组合数 {count}/25。</div>
      {OPTIMIZATION_VARIABLES.map((item) => <div key={item.id} style={{ display: 'grid', gridTemplateColumns: '18px 1fr', gap: 3, alignItems: 'center' }}>
        <input type="checkbox" checked={props.selectedVariables.includes(item.id)} onChange={(event) => {
          const next = event.target.checked ? [...props.selectedVariables, item.id].slice(0, 3) : props.selectedVariables.filter((id) => id !== item.id);
          props.onSelectedVariablesChange(next);
        }} />
        <label style={{ fontSize: 10 }}>{item.label} ({item.unit})<input value={variableValues[item.id]} onChange={(event) => setVariableValues({ ...variableValues, [item.id]: event.target.value })} style={control} /></label>
      </div>)}
      <label style={{ fontSize: 10 }}>优化目标模板<select value={props.objectiveTemplate} onChange={(event) => props.onObjectiveTemplateChange(event.target.value as Props['objectiveTemplate'])} style={control}>
        <option value="BALANCED_STIMULATION">均衡改造</option><option value="LENGTH_PRIORITY">有效缝长优先</option><option value="CONTAINMENT_PRIORITY">缝高控制优先</option><option value="CLUSTER_BALANCE_PRIORITY">簇均衡优先</option>
      </select></label>
      <label style={{ fontSize: 10 }}>推荐数量<select value={props.topN} onChange={(event) => props.onTopNChange(Number(event.target.value) as 3 | 5 | 10)} style={control}><option value={3}>前 3 名</option><option value={5}>前 5 名</option><option value={10}>前 10 名</option></select></label>
      <details><summary style={{ fontSize: 10, cursor: 'pointer' }}>综合评分权重 / 方向 / 极差归一化</summary>{Object.entries(weights).map(([id, value]) => <label key={id} style={{ display: 'grid', gridTemplateColumns: '1fr 52px', gap: 4, fontSize: 10, marginTop: 3 }}><span>{METRIC_LABELS[id] ?? id} {METRIC_DIRECTIONS[id]}{id === 'conductivity' ? ' · 部分可用' : ''}</span><input type="number" min={0} step={0.01} value={value} onChange={(event) => setWeights({ ...weights, [id]: Math.max(0, Number(event.target.value)) })} style={control} /></label>)}</details>
      {count > 25 && <div style={{ color: '#C2533A', fontSize: 10 }}>当前组合数过多；后台不会截断前 25 个。</div>}
      <button onClick={runSweep} disabled={sweepBusy || count > 25} style={button}>{sweepBusy ? '正在运行受控 P3D 参数扫频…' : '运行约束优先方案优化'}</button>
      {sweepResult && <>
        <div style={{ fontSize: 10 }}>完成 {sweepResult.completedCases}/{sweepResult.totalCases} · 失败 {sweepResult.failedCases} · 约束拒绝 {sweepResult.invalidCases}</div>
        <div style={{ overflowX: 'auto' }}><table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 10 }}><thead><tr>{['排名', '方案', '可行性', '提示', '有效半缝长', '控高', '流量均衡', '得分'].map((label) => <th key={label} style={th}>{label}</th>)}</tr></thead><tbody>{sweepResult.schemes.map((scheme) => <tr key={scheme.schemeId}><td style={td}>{scheme.rank ?? '—'}</td><td style={td}>{scheme.schemeId}</td><td style={td}>{FEASIBILITY_LABELS[scheme.feasibilityStatus ?? ''] ?? scheme.feasibilityStatus ?? '—'}</td><td style={td}>{scheme.constraintCount ?? 0}</td><td style={td}>{format(scheme.metrics?.effectiveHalfLength, 1)}</td><td style={td}>{format(scheme.metrics?.heightContainment)}</td><td style={td}>{format(scheme.metrics?.flowBalance)}</td><td style={td}>{format(scheme.score)}</td></tr>)}</tbody></table></div>
        {sweepResult.topSchemes[0] && <div style={helpBox}>
          <b>首选方案推荐理由：</b>{sweepResult.topSchemes[0].recommendationReasons?.length
            ? sweepResult.topSchemes[0].recommendationReasons.map((reason) => RECOMMENDATION_LABELS[reason] ?? reason).join('；')
            : '综合归一化得分最高，且已通过工程可行性筛查。'}
        </div>}
        {sweepResult.topSchemes[0] && <button disabled={variantBusy} onClick={() => applyRecommendedVariant(sweepResult.topSchemes[0])} style={button}>将首选方案创建为新方案变体</button>}
      </>}
    </>}
    {mode === 'COMPARISON' && <>
      {!selectableSchemes.length ? <div style={{ fontSize: 10, color: '#8598AD' }}>请先在压裂设计模块生成可比较方案。</div> : <>
        <div style={{ fontSize: 10 }}>基准方案 + 最多 4 个候选方案；点击表格行会在同一个三维视图中切换。</div>
        {selectableSchemes.map((scheme) => <label key={scheme.schemeId} style={{ fontSize: 10 }}><input type="checkbox" checked={props.comparisonSchemeIds.includes(scheme.schemeId ?? '')} onChange={(event) => {
          const id = scheme.schemeId ?? '';
          const next = event.target.checked ? [...props.comparisonSchemeIds, id].slice(0, 5) : props.comparisonSchemeIds.filter((item) => item !== id);
          updateComparison(next);
        }} /> {scheme.schemeId}{scheme.rank ? ` · 第 ${scheme.rank} 名` : ''}</label>)}
        <div style={{ overflowX: 'auto' }}><table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 10 }}><thead><tr>{['方案', '可行性', '簇间距', '排量', '簇数', '液量', '砂量', '有效半缝长', '缝高', '流量均衡', '起裂均衡', '应力阴影', 'NF相交', '导流估算', '得分'].map((label) => <th key={label} style={th}>{label}</th>)}</tr></thead><tbody>{selectableSchemes.filter((scheme) => props.comparisonSchemeIds.includes(scheme.schemeId ?? '')).map((scheme) => <tr key={scheme.schemeId} onClick={() => scheme.simulationId && props.onSelectSimulation(scheme.simulationId)} style={{ cursor: 'pointer' }}><td style={td}>{scheme.schemeId}</td><td style={td}>{FEASIBILITY_LABELS[scheme.feasibilityStatus ?? ''] ?? '—'}</td><td style={td}>{format(scheme.parameters?.cluster_spacing_m, 1)}</td><td style={td}>{format(scheme.parameters?.pump_rate_m3min, 1)}</td><td style={td}>{format(scheme.parameters?.n_clusters, 0)}</td><td style={td}>{format(scheme.parameters?.fluid_volume_multiplier, 2)}</td><td style={td}>{format(scheme.parameters?.proppant_mass_multiplier, 2)}</td><td style={td}>{format(scheme.metrics?.effectiveHalfLength, 1)}</td><td style={td}>{format(scheme.metrics?.height, 1)}</td><td style={td}>{format(scheme.metrics?.flowBalance)}</td><td style={td}>{format(scheme.metrics?.initiationBalance)}</td><td style={td}>{format(scheme.metrics?.stressShadowInterference)}</td><td style={td}>{format(scheme.metrics?.encounterCount, 0)}</td><td style={td}>{format(scheme.metrics?.conductivity)}</td><td style={td}>{format(scheme.score)}</td></tr>)}</tbody></table></div>
        <button style={button} onClick={() => csvDownload('fracsim-top-n-comparison.csv', selectableSchemes.filter((scheme) => props.comparisonSchemeIds.includes(scheme.schemeId ?? '')).map((scheme) => ({ scheme: scheme.schemeId, feasibility: scheme.feasibilityStatus, warnings: scheme.constraintCount, ...scheme.parameters, ...scheme.metrics, score: scheme.score, simulationId: scheme.simulationId })))}>导出方案对比 CSV</button>
      </>}
    </>}
    {mode === 'FULL_WELL' && (() => {
      const scheme = selectableSchemes.find((item) => props.comparisonSchemeIds.includes(item.schemeId ?? ''))
        ?? sweepResult?.topSchemes[0] ?? sweepResult?.baselineScheme;
      const summary = scheme?.fullWellSummary;
      if (!summary) return <div style={{ fontSize: 10, color: '#8598AD' }}>请先运行优化或选择包含全井段结果的方案。</div>;
      const rows = Array.isArray(summary.stageMatrix) ? summary.stageMatrix as Array<Record<string, any>> : [];
      return <>
        <div style={helpBox}>全井汇总仅聚合各段真实模拟结果，不运行第二套求解器。当前方案：{scheme?.schemeId}</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 3, fontSize: 10 }}>
          <div style={helpBox}>压裂段数<br /><b>{summary.stageCount}</b></div><div style={helpBox}>总簇数<br /><b>{summary.totalClusters}</b></div><div style={helpBox}>总液量<br /><b>{format(summary.totalFluidM3, 1)} m³</b></div>
          <div style={helpBox}>总砂量<br /><b>{format(summary.totalProppantT, 1)} t</b></div><div style={helpBox}>平均有效半缝长<br /><b>{format(summary.averageEffectiveHalfLengthM, 1)} m</b></div><div style={helpBox}>起裂成功率<br /><b>{format(summary.initiationSuccessRatio)}</b></div>
        </div>
        <div style={{ overflowX: 'auto' }}><table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 10 }}><thead><tr>{['段', 'MD', '簇数', '间距', '排量', '液量', '砂量', '半缝长', '缝高', '流量均衡', 'NF相交', '状态'].map((label) => <th key={label} style={th}>{label}</th>)}</tr></thead><tbody>{rows.map((row) => <tr key={String(row.stageNumber)} onClick={() => scheme?.simulationId && props.onSelectSimulation(scheme.simulationId, Number(row.stageNumber))} style={{ cursor: 'pointer' }}><td style={td}>{row.stageNumber}</td><td style={td}>{row.mdTop}–{row.mdBottom}</td><td style={td}>{row.clusterCount}</td><td style={td}>{format(row.clusterSpacingM, 1)}</td><td style={td}>{format(row.rateM3min, 1)}</td><td style={td}>{format(row.fluidM3, 1)}</td><td style={td}>{format(row.proppantT, 1)}</td><td style={td}>{format(row.halfLengthM, 1)}</td><td style={td}>{format(row.heightM, 1)}</td><td style={td}>{format(row.flowBalance)}</td><td style={td}>{format(row.nfEncounters, 0)}</td><td style={td}>{row.status}</td></tr>)}</tbody></table></div>
        <button style={button} onClick={() => csvDownload('fracsim-full-well-summary.csv', rows)}>导出全井汇总 CSV</button>
      </>;
    })()}
  </div>;
}
